import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LiveTvSource } from '@/types/livetv';

interface Props {
  sources: LiveTvSource[];
  activeId: string;
  onSelect(source: LiveTvSource): void;
}

/**
 * Language toggle for a Live TV channel. Same logical channel from different
 * catalogs/countries collapses into one card with several `sources`; this lets
 * the viewer pick their language. One row per language (the first source for
 * that language wins); renders nothing when there's only one source.
 *
 * Reuses the shared `.source-menu` styles (see SportsSourceMenu).
 */
export function LiveTvSourceMenu({ sources, activeId, onSelect }: Props): JSX.Element | null {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // User-facing label: language → country → generic "Original". Never the
  // backend `label` (it falls back to the internal provider name, e.g.
  // "daddylive", when a source has neither language nor country).
  const displayLabel = (s: LiveTvSource): string =>
    s.language ?? s.country ?? t('liveTvSource.original');

  // One row per distinct display label, in first-seen order (primary first).
  const rows = useMemo(() => {
    const seen = new Set<string>();
    const out: LiveTvSource[] = [];
    for (const s of sources) {
      const key = displayLabel(s).toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(s);
    }
    return out;
    // displayLabel only depends on `t`, which is stable per language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources, t]);

  if (rows.length <= 1) return null;

  const active = sources.find((s) => s.id === activeId) ?? rows[0];

  return (
    <div className="source-menu livetv-source-menu">
      <button
        type="button"
        className="btn-ghost"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {t('liveTvSource.trigger', { name: displayLabel(active) })}
      </button>
      {open && (
        <ul role="menu" className="source-menu__list">
          {rows.map((s) => (
            <li key={s.id} role="menuitemradio" aria-checked={s.id === active.id}>
              <button
                type="button"
                className={`source-menu__row${s.id === active.id ? ' is-active' : ''}`}
                onClick={() => { onSelect(s); setOpen(false); }}
              >
                <span>{displayLabel(s)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
